import React, { useEffect, useReducer, useRef, useState } from 'react';
import SafeRedirect from '../../../SafeRedirect';
import { useLinkedinInsightTag } from '../../../../hooks/useLinkedingInsightTag';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { paymentType } from '../PaymentMethod/PaymentMethod';
import { InjectAppServices } from '../../../../services/pure-di';
import { Loading } from '../../../Loading/Loading';
import { useQueryParams } from '../../../../hooks/useQueryParams';
import {
  checkoutSummaryReducer,
  CHECKOUT_SUMMARY_ACTIONS,
  INITIAL_STATE_CHECKOUT_SUMMARY,
} from '../Reducers/checkoutSummaryReducer';
import { exception } from 'react-ga';
import { UnexpectedError } from '../../PlanCalculator/UnexpectedError';
import { ACCOUNT_TYPE, thousandSeparatorNumber } from '../../../../utils';
import { TransferInformation } from './TransferInformation/index';
import { CheckoutSummaryTitle } from './CheckoutSummaryTitle/index';
import { MercadoPagoInformation } from './MercadoPagoInformation';
import {
  AddOnType,
  BUY_CHAT_PLAN,
  BUY_LANDING_PACK,
  BUY_MARKETING_PLAN,
  BUY_ONSITE_PLAN,
  BUY_PUSH_NOTIFICATION_PLAN,
  PLAN_TYPE,
} from '../../../../doppler-types';
import { Link } from 'react-router-dom';
import { AddOn } from '../../../shared/AddOn';
import { useFetchLandingPacks } from '../../../../hooks/useFetchtLandingPacks';
import useTimeout from '../../../../hooks/useTimeout';
import { Slide } from '../../../Dashboard/LearnWithDoppler/Carousel/Slide/Slide';
import { CheckoutSummaryCarousel } from './CheckoutSummaryCarousel';
import { AddOnPlanInformation } from './AddOnPlanInformation';
import { useAddOnPlans } from '../../../../hooks/useFetchAddOnPlans';
import { AddOnsSection } from '../../../BuyProcess/NewPlanSelection/AddOnsSection';
import { NewPlanSelectionStyled } from '../../../BuyProcess/NewPlanSelection/index.styles';
import { CheckoutSummaryHeader, CheckoutSummaryLayout } from './CheckoutSummary.styles';

const MIN_ADD_ONS_PANEL_HEIGHT = 640;

export const AddOnLandingPack = InjectAppServices(
  ({ dependencies: { dopplerAccountPlansApiClient } }) => {
    const intl = useIntl();
    const _ = (id, values) => intl.formatMessage({ id: id }, values);
    const { cheapestLandingPack } = useFetchLandingPacks(dopplerAccountPlansApiClient);
    const query = useQueryParams();
    const accountType = query.get(ACCOUNT_TYPE) ?? '';

    return (
      <AddOn
        title={_('landing_selection.modal.title')}
        titleIconName="dpicon iconapp-landing-page"
        description={_('landing_selection.modal.description')}
        link1={{
          pathname: `/landing-packages${
            accountType
              ? `?${ACCOUNT_TYPE}=${accountType}&buyType=${BUY_LANDING_PACK}`
              : `?buyType=${BUY_LANDING_PACK}`
          }`,
          label: `${_('landing_selection.modal.more_information_label')}`,
        }}
        link2={{
          pathname: `/landing-packages${
            accountType
              ? `?${ACCOUNT_TYPE}=${accountType}&buyType=${BUY_LANDING_PACK}`
              : `?buyType=${BUY_LANDING_PACK}`
          }`,
          label: `${_('landing_selection.modal.buy_now_button')}`,
        }}
        priceComponent={
          <FormattedMessage
            id="landing_selection.pack_from"
            values={{
              price: cheapestLandingPack?.price || 0,
            }}
          />
        }
      />
    );
  },
);

export const AddOnPlan = InjectAppServices(
  ({ addOnType, dependencies: { dopplerAccountPlansApiClient, appSessionRef } }) => {
    const intl = useIntl();
    const _ = (id, values) => intl.formatMessage({ id: id }, values);
    const [{ cheapestAddOnPlan }] = useAddOnPlans(
      addOnType,
      dopplerAccountPlansApiClient,
      appSessionRef,
    );

    const title =
      addOnType === AddOnType.OnSite
        ? _('onsite_selection.card.title')
        : _('push_notification_selection.card.title');

    const titleIconName =
      addOnType === AddOnType.OnSite ? 'dpicon iconapp-online-clothing' : 'dpicon iconapp-bell1';

    const description =
      addOnType === AddOnType.OnSite
        ? _('onsite_selection.card.description')
        : _('push_notification_selection.card.description');

    const link1 =
      addOnType === AddOnType.OnSite
        ? {
            pathname: `/buy-onsite-plans?buyType=${BUY_ONSITE_PLAN}`,
            label: `${_('onsite_selection.card.more_information_label')}`,
          }
        : {
            pathname: `/buy-push-notification-plans?buyType=${BUY_PUSH_NOTIFICATION_PLAN}`,
            label: `${_('push_notification_selection.card.more_information_label')}`,
          };

    const link2 =
      addOnType === AddOnType.OnSite
        ? {
            pathname: `/buy-onsite-plans?buyType=${BUY_ONSITE_PLAN}`,
            label: `${_('onsite_selection.card.buy_now_button')}`,
          }
        : {
            pathname: `/buy-push-notification-plans?buyType=${BUY_PUSH_NOTIFICATION_PLAN}`,
            label: `${_('push_notification_selection.card.buy_now_button')}`,
          };

    const planFromMessageId =
      addOnType === AddOnType.OnSite
        ? 'onsite_selection.card.plan_from'
        : 'push_notification_selection.card.plan_from';

    return (
      <AddOn
        title={title}
        titleIconName={titleIconName}
        description={description}
        link1={link1}
        link2={link2}
        priceComponent={
          <FormattedMessage
            id={planFromMessageId}
            values={{
              price: cheapestAddOnPlan?.fee || 0,
            }}
          />
        }
      />
    );
  },
);

export const FormatMessageWithSpecialStyle = ({ id }) => {
  return (
    <FormattedMessage
      id={id}
      values={{
        underline: (chunks) => <u>{chunks}</u>,
        bold: (chunks) => <b>{chunks}</b>,
      }}
    />
  );
};

const getTitle = (paymentMethod, upgradePending) => {
  if (paymentMethod === paymentType.transfer && upgradePending) {
    return {
      smallTitle: 'checkoutProcessSuccess.transfer_purchase_finished_title',
      largeTitle: 'checkoutProcessSuccess.transfer_title',
      description: 'checkoutProcessSuccess.transfer_warning_message',
    };
  } else {
    if (paymentMethod === paymentType.transfer) {
      return {
        smallTitle: 'checkoutProcessSuccess.transfer_purchase_finished_title',
        largeTitle: 'checkoutProcessSuccess.transfer_upgrade_title',
        description: 'checkoutProcessSuccess.transfer_upgrade_warning_message',
      };
    } else {
      if (paymentMethod === paymentType.mercadoPago && upgradePending) {
        return {
          smallTitle: 'checkoutProcessSuccess.mercado_pago_purchase_finished_title',
          largeTitle: 'checkoutProcessSuccess.transfer_title',
          description: 'checkoutProcessSuccess.mercado_pago_warning_message',
        };
      }
    }
  }
  return {
    smallTitle: 'checkoutProcessSuccess.purchase_finished_title',
    largeTitle: 'checkoutProcessSuccess.title',
  };
};

const PlanBuyMessage = ({ title, paymentMethod, upgradePending }) => {
  const intl = useIntl();
  const _ = (id, values) => intl.formatMessage({ id: id }, values);

  return (
    <>
      <div className={`dp-wrap-message dp-wrap-success`}>
        <span className="dp-message-icon" />
        <div className="dp-content-message dp-content-full">
          <p>
            <strong>{_(title.largeTitle)}</strong>
            <br />
            {[paymentType.mercadoPago, paymentType.transfer].includes(paymentMethod) &&
            upgradePending
              ? _(title.description)
              : _('checkoutProcessSuccess.enjoying_benefits_doppler_title')}
          </p>
          <Link to="/dashboard" className="dp-message-link">
            {_('checkoutProcessSuccess.go_to_home_link')}
          </Link>
        </div>
      </div>
    </>
  );
};

const PlanMarketingInformation = ({
  planType,
  quantity,
  discount,
  extraCredits,
  remainingCredits,
}) => {
  const intl = useIntl();
  const _ = (id, values) => intl.formatMessage({ id: id }, values);
  const quantityUnitByPlanType = {
    [PLAN_TYPE.byContact]: _('checkoutProcessSuccess.plan_type_subscribers_unit'),
    [PLAN_TYPE.byEmail]: _('checkoutProcessSuccess.plan_type_monthly_deliveries_unit'),
    [PLAN_TYPE.byCredit]: _('checkoutProcessSuccess.plan_type_prepaid_unit'),
  };

  return (
    <>
      <h4 className="dp-tit-plan-purchased">
        <span className="checkout-summary-desktop-title">
          {_(`checkoutProcessSuccess.plan_type_title`)}
        </span>
        <span className="checkout-summary-mobile-title">
          {_(`checkoutProcessSuccess.email_marketing_plan_title`)}
        </span>
      </h4>
      <ul className="dp-purchase-summary-list">
        <li>
          <span>{_(`checkoutProcessSuccess.plan_type`)}</span>
          <h3>{_(`checkoutProcessSuccess.plan_type_${planType.replace('-', '_')}_label`)}</h3>
        </li>
        <li>
          <span>{_('checkoutProcessSuccess.quantity_title')}</span>
          <h3>
            {thousandSeparatorNumber(intl.defaultLocale, quantity)}
            {quantityUnitByPlanType[planType] ? ` ${quantityUnitByPlanType[planType]}` : ''}
          </h3>
        </li>
        {extraCredits > 0 ? (
          <li>
            <span>{_(`checkoutProcessSuccess.plan_type_prepaid_promocode`)}</span>
            <h3>{thousandSeparatorNumber(intl.defaultLocale, extraCredits)}</h3>
          </li>
        ) : null}
        <li>
          {planType === PLAN_TYPE.byContact && discount ? (
            <>
              <span>{_(`checkoutProcessSuccess.renewal_type_title`)}</span>
              <h3>{_('checkoutProcessSuccess.discount_' + discount?.replace('-', '_'))}</h3>
            </>
          ) : planType === PLAN_TYPE.byEmail ? (
            <>
              <span>{_(`checkoutProcessSuccess.renewal_type_title`)}</span>
              <h3>{_(`checkoutProcessSuccess.plan_type_monthly_deliveries_monthly_renovation`)}</h3>
            </>
          ) : planType === PLAN_TYPE.byCredit ? (
            <>
              <span>{_(`checkoutProcessSuccess.renewal_type_title`)}</span>
              <h3>{_(`checkoutProcessSuccess.plan_type_prepaid_single_payment`)}</h3>
            </>
          ) : (
            <h3>{_(`checkoutProcessSuccess.plan_type_prepaid_no_expiration`)}</h3>
          )}
        </li>
      </ul>
    </>
  );
};

const subscription_types = {
  discount_1: 'discount_monthly',
  discount_3: 'discount_quarterly',
  discount_6: 'discount_half_yearly',
  discount_12: 'discount_yearly',
};

const PlanLandingPagesInformation1 = InjectAppServices(
  ({ dependencies: { dopplerAccountPlansApiClient, appSessionRef } }) => {
    const intl = useIntl();
    const _ = (id, values) => intl.formatMessage({ id: id }, values);
    const query = useQueryParams();
    const landingIdsStr = query.get('landing-ids') ?? '';
    const landingsQtyStr = query.get('landing-packs') ?? '';
    const { error, loading, landingPacks } = useFetchLandingPacks(dopplerAccountPlansApiClient);

    if (loading) {
      return <Loading page />;
    }

    if (error) {
      return <UnexpectedError />;
    }

    const landingIds = landingIdsStr?.split(',') ?? [];
    const landingsQty = landingsQtyStr?.split(',') ?? [];
    const selectedLandings = landingPacks
      ?.filter((lp) => landingIds.includes(lp.planId?.toString()))
      ?.map((lp, index) => ({
        ...lp,
        packagesQty: Number(landingsQty[index]),
      }));

    if (!selectedLandings?.length) {
      return <></>;
    }

    console.log('allLandingPacks desde PlanLandingPagesInformation', landingPacks);
    console.log('selectedLandings desde PlanLandingPagesInformation', selectedLandings);

    const { planSubscription } = appSessionRef.current.userData.user.plan;

    return (
      <>
        <h4 className="dp-tit-plan-purchased">
          {_(`checkoutProcessSuccess.landing_your_landings_pages_plan_title`)}
        </h4>
        <ul className="dp-purchase-summary-list checkout-summary-landing-desktop">
          <li>
            <span>{_(`checkoutProcessSuccess.plan_type`)}</span>
            {selectedLandings.map((landingPack, index) => (
              <React.Fragment key={`landing-pack${index}`}>
                <h4>
                  <FormattedMessage
                    id="landing_selection.pack_of_landing_pages_with_plural"
                    values={{
                      packs: landingPack.landingsQty,
                    }}
                  />
                </h4>
              </React.Fragment>
            ))}
          </li>
          <li>
            <span>{_(`checkoutProcessSuccess.landing_packages_title`)}</span>
            {selectedLandings.map((landingPack, index) => (
              <React.Fragment key={`landing-pack${index}`}>
                <h4>{landingPack.packagesQty}</h4>
              </React.Fragment>
            ))}
          </li>
          <li>
            <span>{_(`checkoutProcessSuccess.landing_billing_title`)}</span>
            {selectedLandings.map((landingPack, index) => (
              <React.Fragment key={`landing-pack${index}`}>
                <h4>{_('buy_process.' + subscription_types[`discount_${planSubscription}`])}</h4>
              </React.Fragment>
            ))}
          </li>
        </ul>
        <ul className="dp-purchase-summary-list checkout-summary-landing-mobile">
          <li className="checkout-summary-landing-detail-label">
            <span>{_(`checkoutProcessSuccess.plan_type`)}</span>
          </li>
          {selectedLandings.map((landingPack, index) => (
            <li className="checkout-summary-landing-pack" key={`landing-pack-summary-${index}`}>
              <h4>
                <FormattedMessage
                  id="landing_selection.pack_of_landing_pages_with_plural"
                  values={{
                    packs: landingPack.landingsQty,
                  }}
                />
              </h4>
              <div className="checkout-summary-landing-pack-data">
                <div>
                  <span>{_(`checkoutProcessSuccess.landing_packages_title`)}</span>
                  <h4>{landingPack.packagesQty}</h4>
                </div>
                <div>
                  <span>{_(`checkoutProcessSuccess.landing_billing_title`)}</span>
                  <h4>{_('buy_process.' + subscription_types[`discount_${planSubscription}`])}</h4>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </>
    );
  },
);

export const PlanLandingPagesInformation2 = InjectAppServices(
  ({ dependencies: { dopplerAccountPlansApiClient, appSessionRef } }) => {
    const intl = useIntl();
    const _ = (id, values) => intl.formatMessage({ id: id }, values);
    const query = useQueryParams();
    const landingIdsStr = query.get('landing-ids') ?? '';
    const landingsQtyStr = query.get('landing-packs') ?? '';
    const { error, loading, landingPacks } = useFetchLandingPacks(dopplerAccountPlansApiClient);

    if (loading) {
      return <Loading page />;
    }

    if (error) {
      return <UnexpectedError />;
    }

    const landingIds = landingIdsStr?.split(',') ?? [];
    const landingsQty = landingsQtyStr?.split(',') ?? [];
    const selectedLandings = landingPacks
      ?.filter((lp) => landingIds.includes(lp.planId?.toString()))
      ?.map((lp, index) => ({
        ...lp,
        packagesQty: Number(landingsQty[index]),
      }));

    if (!selectedLandings?.length) {
      return <></>;
    }

    console.log('allLandingPacks desde PlanLandingPagesInformation', landingPacks);
    console.log('selectedLandings desde PlanLandingPagesInformation', selectedLandings);

    const { planSubscription } = appSessionRef.current.userData.user.plan;

    return (
      <>
        <h4 className="dp-tit-plan-purchased">Tu plan de Landings Pages</h4>
        {selectedLandings.map((landingPack, index) => (
          <ul className="dp-purchase-summary-list">
            <li>
              <span>{_(`checkoutProcessSuccess.plan_type`)}</span>
              <h4>
                <FormattedMessage
                  id="landing_selection.pack_of_landing_pages_with_plural"
                  values={{
                    packs: landingPack.landingsQty,
                  }}
                />
              </h4>
            </li>
            <li>
              <span>Paquetes</span>
              <h4>{landingPack.packagesQty}</h4>
            </li>
            <li>
              <span>Facturación</span>
              <h4>{_('buy_process.' + subscription_types[`discount_${planSubscription}`])}</h4>
            </li>
          </ul>
        ))}
      </>
    );
  },
);

export const CheckoutSummary = InjectAppServices(
  ({
    dependencies: { dopplerBillingUserApiClient, dopplerAccountPlansApiClient, appSessionRef },
    location,
  }) => {
    useLinkedinInsightTag();
    const checkoutSummaryMainRef = useRef(null);
    const [addOnsPanelHeight, setAddOnsPanelHeight] = useState(null);
    const [
      {
        loading,
        billingCountry,
        paymentMethod,
        planType,
        discount,
        quantity,
        extraCredits,
        remainingCredits,
        hasError,
        chatUserPlan,
        addOnUserPlan,
      },
      dispatch,
    ] = useReducer(checkoutSummaryReducer, INITIAL_STATE_CHECKOUT_SUMMARY);

    const query = useQueryParams();
    const redirect = query.get('redirect');
    const legacy = query.get('legacy');
    const planId = query.get('planId') ?? '';
    const paymentMethodType = query.get('paymentMethod') ?? '';
    const discountDescription = query.get('discount') ?? '';
    const extraCreditsByPromocode = query.get('extraCredits') ?? 0;
    const promocodeFromUrl = query.get('promo-code') ?? query.get('PromoCode') ?? '';
    const buyType = query.get('buyType') ?? '';
    const intl = useIntl();
    const _ = (id, values) => intl.formatMessage({ id: id }, values);
    const total = sessionStorage.getItem('amount');
    const upgradePending = appSessionRef.current.userData.user.plan.upgradePending;

    useEffect(() => {
      const mainPanel = checkoutSummaryMainRef.current;

      if (!mainPanel) {
        return undefined;
      }

      const updateAddOnsPanelHeight = () => {
        const isMobile = window.matchMedia?.('(max-width: 991px)').matches ?? false;
        const mainPanelHeight = mainPanel.getBoundingClientRect().height;

        setAddOnsPanelHeight(
          !isMobile && mainPanelHeight > 0
            ? Math.max(mainPanelHeight + 46, MIN_ADD_ONS_PANEL_HEIGHT)
            : null,
        );
      };

      updateAddOnsPanelHeight();
      window.addEventListener('resize', updateAddOnsPanelHeight);

      if (!window.ResizeObserver) {
        return () => window.removeEventListener('resize', updateAddOnsPanelHeight);
      }

      const resizeObserver = new window.ResizeObserver(updateAddOnsPanelHeight);
      resizeObserver.observe(mainPanel);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateAddOnsPanelHeight);
      };
    }, [loading, hasError]);

    useEffect(() => {
      const fetchBillingInformationData = async () => {
        const data = await dopplerBillingUserApiClient.getBillingInformationData();
        if (data.success) {
          return data;
        } else {
          throw new exception();
        }
      };

      const fetchCurrentUserPlanByType = async (type) => {
        const data = await dopplerBillingUserApiClient.getCurrentUserPlanDataByType(type);
        if (data.success) {
          return data.value;
        } else {
          return null;
        }
      };

      const fetchAddOnPromotionsByPromocode = async (planId, promocode) => {
        const data = await dopplerAccountPlansApiClient.getAddOnPromotionsByPromocode(
          planId,
          promocode,
        );
        if (data.success) {
          return data.value;
        } else {
          return null;
        }
      };

      const fetchData = async () => {
        try {
          dispatch({ type: CHECKOUT_SUMMARY_ACTIONS.START_FETCH });
          const billingInformationData = await fetchBillingInformationData();
          const currentUserPlanData = await fetchCurrentUserPlanByType(1);

          var addOnPromotions = undefined;
          if (buyType && Number(buyType) === BUY_MARKETING_PLAN) {
            if (promocodeFromUrl !== '') {
              addOnPromotions = await fetchAddOnPromotionsByPromocode(planId, promocodeFromUrl);
            }
          }

          var currentAddOnPlanUserData = undefined;
          if (buyType && Number(buyType) === BUY_ONSITE_PLAN) {
            currentAddOnPlanUserData = await fetchCurrentUserPlanByType(4);
          }

          if (buyType && Number(buyType) === BUY_PUSH_NOTIFICATION_PLAN) {
            currentAddOnPlanUserData = await fetchCurrentUserPlanByType(5);
          }

          if (buyType && Number(buyType) === BUY_CHAT_PLAN) {
            currentAddOnPlanUserData = await fetchCurrentUserPlanByType(2);
          }

          dispatch({
            type: CHECKOUT_SUMMARY_ACTIONS.FINISH_FETCH,
            payload: {
              billingInformation: billingInformationData.value,
              currentUserPlan: currentUserPlanData,
              extraCredits: extraCreditsByPromocode,
              discount: discountDescription,
              paymentMethod: paymentMethodType,
              addOnUserPlan: currentAddOnPlanUserData,
              addOnPromotions: addOnPromotions,
            },
          });
        } catch (error) {
          dispatch({ type: CHECKOUT_SUMMARY_ACTIONS.FAIL_FETCH });
        }
      };

      fetchData();
    }, [
      dopplerBillingUserApiClient,
      dopplerAccountPlansApiClient,
      extraCreditsByPromocode,
      discountDescription,
      paymentMethodType,
      planId,
      buyType,
      promocodeFromUrl,
    ]);

    if (legacy) {
      if (redirect) {
        return <SafeRedirect to={`/${redirect}`} />;
      }
      return <SafeRedirect to="/Campaigns/Draft" />;
    }

    if (loading) {
      return <Loading page />;
    }

    if (hasError) {
      return <UnexpectedError />;
    }

    const title = getTitle(paymentMethod, upgradePending);
    const landingsEditorEnabled = appSessionRef?.current?.userData?.features?.landingsEditorEnabled;

    return (
      <>
        <Helmet>
          <title>{_('checkoutProcessSuccess.purchase_finished_title')}</title>
          <meta name="checkout-success" />
        </Helmet>
        <CheckoutSummaryHeader>
          <CheckoutSummaryTitle title={title} hideBreadcrumb={true} />
        </CheckoutSummaryHeader>
        <section className="dp-container">
          <CheckoutSummaryLayout>
            <div className="checkout-summary-main m-b-24" ref={checkoutSummaryMainRef}>
              <PlanBuyMessage
                title={title}
                paymentMethod={paymentMethod}
                upgradePending={upgradePending}
              />
              {buyType && Number(buyType) === BUY_MARKETING_PLAN ? (
                <>
                  <PlanMarketingInformation
                    planType={planType}
                    quantity={quantity}
                    discount={discount}
                    extraCredits={extraCredits}
                    remainingCredits={remainingCredits}
                  />
                </>
              ) : buyType && Number(buyType) === BUY_CHAT_PLAN ? (
                chatUserPlan !== null && (
                  <AddOnPlanInformation
                    quantity={
                      <FormattedMessage
                        id={'checkoutProcessSuccess.chat_plan_quantity_title'}
                        values={{
                          quantity: thousandSeparatorNumber(
                            intl.defaultLocale,
                            addOnUserPlan.quantity,
                          ),
                        }}
                      />
                    }
                    discount={discount}
                    addOnType={AddOnType.Conversations}
                  />
                )
              ) : buyType && Number(buyType) === BUY_ONSITE_PLAN && addOnUserPlan !== null ? (
                <AddOnPlanInformation
                  quantity={
                    <FormattedMessage
                      id={'checkoutProcessSuccess.onsite_plan_quantity_title'}
                      values={{
                        quantity: thousandSeparatorNumber(
                          intl.defaultLocale,
                          addOnUserPlan.quantity,
                        ),
                      }}
                    />
                  }
                  discount={discount}
                  addOnType={AddOnType.OnSite}
                />
              ) : buyType &&
                Number(buyType) === BUY_PUSH_NOTIFICATION_PLAN &&
                addOnUserPlan !== null ? (
                <AddOnPlanInformation
                  quantity={
                    <FormattedMessage
                      id={'checkoutProcessSuccess.push_notification_plan_quantity_title'}
                      values={{
                        quantity: thousandSeparatorNumber(
                          intl.defaultLocale,
                          addOnUserPlan.quantity,
                        ),
                      }}
                    />
                  }
                  discount={discount}
                  addOnType={AddOnType.PushNotifications}
                />
              ) : (
                <PlanLandingPagesInformation1 />
              )}
              {paymentMethod === paymentType.transfer ? (
                <TransferInformation
                  billingCountry={billingCountry}
                  upgradePending={upgradePending}
                  lang={appSessionRef.current.userData.user.lang}
                  total={total}
                />
              ) : paymentMethod === paymentType.mercadoPago ? (
                <MercadoPagoInformation upgradePending={upgradePending} />
              ) : null}
            </div>
            {landingsEditorEnabled && (
              <aside
                className="checkout-summary-addons"
                data-testid="checkout-summary-addons"
                style={addOnsPanelHeight ? { height: `${addOnsPanelHeight}px` } : undefined}
              >
                <NewPlanSelectionStyled>
                  <AddOnsSection displayMode="sidebar" showBuyButtons />
                </NewPlanSelectionStyled>
              </aside>
            )}
          </CheckoutSummaryLayout>
        </section>
      </>
    );
  },
);

export const ModalPromoAddons = InjectAppServices(
  ({ addOnPromotions, dependencies: { appSessionRef } }) => {
    const [open, setOpen] = useState(false);
    const intl = useIntl();
    const _ = (id, values) => intl.formatMessage({ id: id }, values);
    const query = useQueryParams();
    const accountType = query.get(ACCOUNT_TYPE) ?? '';
    const createTimeout = useTimeout();
    const { user, features } = appSessionRef.current.userData;
    const landingsEditorEnabled = features?.landingsEditorEnabled;
    const hasLandingPlan = (user.landings?.landingPacks?.length ?? 0) > 0;
    const hasConversationPlan = user.chat?.active === true && user.chat?.plan?.fee > 0;
    const conversationsBuyUrl = user.chat.plan.buttonUrl;
    const canBuyOnSitePlan = process.env.REACT_APP_DOPPLER_CAN_BUY_ONSITE_PLAN === 'true';
    const hasOnSitePlan = user.onSite?.active === true && user.onSite?.plan?.fee > 0;
    const canBuyPushNotificationPlan =
      process.env.REACT_APP_DOPPLER_CAN_BUY_PUSHNOTIFICATION_PLAN === 'true';
    const hasPushNotificationPlan =
      user.pushNotification?.active === true && user.pushNotification?.plan?.fee > 0;
    const canBuyEcoAIPlan = features?.ecoIAEnabled;

    const getAddOnPromotionSliceTitle = (addOnPromotions) => {
      var title = (
        <FormattedMessage
          id={'addons.carousel.slice_5_description_title'}
          values={{
            Bold: (chunk) => <strong>{chunk}</strong>,
            br: <br />,
          }}
        />
      );

      var partOne = title;

      addOnPromotions.forEach((addOnPromotion, index) => {
        partOne = (
          <FormattedMessage
            id="parent_message"
            defaultMessage="{partOne} {partTwo}"
            values={{
              partOne: partOne,
              partTwo: (
                <FormattedMessage
                  id={'addons.carousel.slice_5_description_item'}
                  values={{
                    Bold: (chunk) => <strong>{chunk}</strong>,
                    br: <br />,
                    addOnType: addOnPromotion.idAddOnType,
                    discount: addOnPromotion.discountPercentage,
                    includedAllPlans: addOnPromotion.idAddOnPlan === null,
                    quantity: addOnPromotion.quantity,
                  }}
                />
              ),
            }}
          />
        );
      });

      return partOne;
    };

    const getAddOnsWithPromotion = (addOnPromotions) => {
      const promotions = addOnPromotions.filter(
        (p) =>
          p.idAddOnType === AddOnType.Conversations ||
          p.idAddOnType === AddOnType.Landings ||
          (p.idAddOnType === AddOnType.OnSite && canBuyOnSitePlan) ||
          (p.idAddOnType === AddOnType.PushNotifications && canBuyPushNotificationPlan) ||
          (p.idAddOnType === AddOnType.EcoAI && canBuyEcoAIPlan),
      );

      return promotions;
    };

    const getAddonSlides = () => {
      const slides = [];
      const promotions = getAddOnsWithPromotion(
        addOnPromotions === undefined ? [] : addOnPromotions,
      );

      if (promotions.length > 0) {
        var addOnPromotionSlice = {
          id: 5,
          img: 'addons-carousel-slide-5.png',
          title: 'addons.carousel.slice_5_title',
          description: getAddOnPromotionSliceTitle(promotions),
          link: '/my-plan',
          button_text: 'addons.carousel.slice_5_button_text',
          link_text: 'addons.carousel.slice_5_link_text',
        };

        slides.push(addOnPromotionSlice);
      }

      if (!hasConversationPlan) {
        slides.push({
          id: 1,
          img: 'addons-carousel-slide-1.jpg',
          title: 'addons.carousel.slice_1_title',
          description: (
            <FormattedMessage
              id={'addons.carousel.slice_1_description'}
              values={{
                Bold: (chunk) => <strong>{chunk}</strong>,
                br: <br />,
              }}
            />
          ),
          link: conversationsBuyUrl,
          button_text: 'addons.carousel.slice_1_button_text',
          link_text: 'addons.carousel.slice_1_link_text',
        });
      }

      if (!hasLandingPlan && landingsEditorEnabled) {
        slides.push({
          id: 2,
          img: 'addons-carousel-slide-2.jpg',
          title: 'addons.carousel.slice_2_title',
          description: (
            <FormattedMessage
              id={'addons.carousel.slice_2_description'}
              values={{
                Bold: (chunk) => <strong>{chunk}</strong>,
                br: <br />,
              }}
            />
          ),
          link: `/landing-packages${
            accountType
              ? `?${ACCOUNT_TYPE}=${accountType}&buyType=${BUY_LANDING_PACK}`
              : `?buyType=${BUY_LANDING_PACK}`
          }`,
          button_text: 'addons.carousel.slice_2_button_text',
          link_text: 'addons.carousel.slice_2_link_text',
        });
      }

      if (canBuyOnSitePlan && !hasOnSitePlan) {
        slides.push({
          id: 3,
          img: 'addons-carousel-slide-3.jpg',
          title: 'addons.carousel.slice_3_title',
          description: (
            <FormattedMessage
              id={'addons.carousel.slice_3_description'}
              values={{
                Bold: (chunk) => <strong>{chunk}</strong>,
                br: <br />,
              }}
            />
          ),
          link: `/buy-onsite-plans?buyType=${BUY_ONSITE_PLAN}`,
          button_text: 'addons.carousel.slice_3_button_text',
          link_text: 'addons.carousel.slice_3_link_text',
        });
      }

      if (canBuyPushNotificationPlan && !hasPushNotificationPlan) {
        slides.push({
          id: 4,
          img: `addons-carousel-slide-4--${user.lang === 'es' ? 'es' : 'en'}.png`,
          title: 'addons.carousel.slice_4_title',
          description: (
            <FormattedMessage
              id={'addons.carousel.slice_4_description'}
              values={{
                Bold: (chunk) => <strong>{chunk}</strong>,
                br: <br />,
              }}
            />
          ),
          link: `/buy-push-notification-plans?buyType=${BUY_PUSH_NOTIFICATION_PLAN}`,
          button_text: 'addons.carousel.slice_4_button_text',
          link_text: 'addons.carousel.slice_4_link_text',
        });
      }

      return slides;
    };

    useEffect(() => {
      createTimeout(() => setOpen(true), 800);
    }, [createTimeout]);

    const closeModal = () => setOpen(false);

    const addonSlides = getAddonSlides();

    if (!open || addonSlides.length === 0) {
      return <></>;
    }

    return (
      <div className="modal bg-opacity--50" id="modal-addons">
        <div className="modal-content--medium">
          <span className="close" onClick={closeModal}></span>
          <CheckoutSummaryCarousel
            id="1"
            ariaLabel="addons-packs"
            numberOfItems={addonSlides.length}
            showDots={addonSlides.length > 1}
          >
            {({ activeSlide }) =>
              addonSlides.map((slide, index) => (
                <Slide key={slide.id} active={activeSlide === index} order={index}>
                  <div className="pic-carousel">
                    <img
                      src={_('common.ui_library_image', { imageUrl: slide.img })}
                      alt="Check list"
                      width={'100%'}
                    />
                  </div>
                  <div className="text-carousel">
                    <h3>
                      <FormattedMessage
                        id={slide.title}
                        values={{
                          br: <br />,
                        }}
                      />
                    </h3>
                    <p>
                      {slide.description}
                      {/* <FormattedMessage
                      id={slide.description}
                      values={{
                        Bold: (chunk) => <strong>{chunk}</strong>,
                        br: <br />,
                      }}
                    /> */}
                    </p>
                  </div>
                  <Link to={slide.link} className="dp-button button-medium primary-green m-t-12">
                    {_(slide.button_text)}
                  </Link>
                  <button className="dp-button button-small link-green m-b-12" onClick={closeModal}>
                    {_(slide.link_text)}
                  </button>
                </Slide>
              ))
            }
          </CheckoutSummaryCarousel>
        </div>
      </div>
    );
  },
);
